import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Send, CheckCircle2, CreditCard, PackageCheck, RotateCcw, Star, PlusCircle, Inbox, ThumbsUp, Wallet, TrendingUp, ArrowRight } from "lucide-react";

const RENTER_STEPS = [
  { icon: Search, title: "Find What You Need", body: "Search thousands of local listings by category, location, or keyword." },
  { icon: Send, title: "Submit a Booking Request", body: "Choose your rental dates and submit a booking request to the owner." },
  { icon: CheckCircle2, title: "Get Approved", body: "The owner reviews your request and decides whether to accept it." },
  { icon: CreditCard, title: "Secure Payment", body: "Once approved, complete payment securely through the platform. Any applicable deposit will also be processed." },
  { icon: PackageCheck, title: "Collect and Enjoy", body: "Arrange collection with the owner and enjoy the item during your rental period." },
  { icon: RotateCcw, title: "Return the Item", body: "Return the item in the same condition and on time." },
  { icon: Star, title: "Leave a Review", body: "Help build trust within the SelLend community by reviewing your experience." },
];

const OWNER_STEPS = [
  { icon: PlusCircle, title: "List Your Item", body: "Create a listing with photos, pricing, and availability." },
  { icon: Inbox, title: "Receive Requests", body: "Interested renters submit booking requests." },
  { icon: ThumbsUp, title: "Approve or Decline", body: "Choose which requests you want to accept." },
  { icon: Wallet, title: "Get Paid", body: "Receive payment securely through the platform after a successful booking." },
  { icon: TrendingUp, title: "Build Your Reputation", body: "Earn reviews and ratings to increase future bookings." },
];

function Steps({ steps, testid }) {
  return (
    <ol data-testid={testid} className="relative border-l border-black/10 ml-4 space-y-8">
      {steps.map((s, i) => (
        <motion.li
          key={s.title} data-testid={`${testid}-step-${i}`}
          initial={{ opacity: 0, x: 12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.04 }}
          className="ml-8 relative"
        >
          <span className="absolute -left-[3.15rem] top-0 w-9 h-9 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center text-sm font-bold ring-4 ring-white">
            {i + 1}
          </span>
          <div className="bg-white border border-black/10 rounded-2xl p-5 flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-[#FFF0E8] flex items-center justify-center shrink-0">
              <s.icon className="w-5 h-5 text-[#FF5A00]" />
            </div>
            <div>
              <h3 className="font-semibold">{s.title}</h3>
              <p className="text-muted-foreground text-sm mt-1 leading-relaxed">{s.body}</p>
            </div>
          </div>
        </motion.li>
      ))}
    </ol>
  );
}

export default function HowItWorks() {
  return (
    <div data-testid="how-it-works-page" className="min-h-screen">
      <section className="max-w-4xl mx-auto px-5 sm:px-8 pt-16 pb-10">
        <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A00] mb-5">How it works</span>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[0.95]">
          Renting made simple,<br /><span className="text-[#FF5A00]">for everyone.</span>
        </h1>
        <p className="text-lg text-muted-foreground mt-6 leading-relaxed max-w-xl">
          Whether you're borrowing gear for a weekend or earning from tools sitting in the garage, here's exactly how it works.
        </p>
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-12 grid lg:grid-cols-2 gap-14">
        <div>
          <div className="flex items-center gap-2 mb-8">
            <span className="text-xs font-bold uppercase tracking-[0.15em] bg-[#E3E8E1] px-3 py-1 rounded-full">For Renters</span>
          </div>
          <Steps steps={RENTER_STEPS} testid="renter-steps" />
        </div>
        <div>
          <div className="flex items-center gap-2 mb-8">
            <span className="text-xs font-bold uppercase tracking-[0.15em] bg-[#FFF0E8] text-[#FF5A00] px-3 py-1 rounded-full">For Owners</span>
          </div>
          <Steps steps={OWNER_STEPS} testid="owner-steps" />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="bg-[#0A0A0A] text-white rounded-3xl p-10 sm:p-14 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Start in minutes</h2>
          <p className="text-white/60 mt-3 max-w-lg mx-auto">Find what you need nearby, or list your first item and start earning today.</p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <Link to="/browse" data-testid="how-cta-browse" className="flex items-center gap-1.5 bg-[#FF5A00] text-white rounded-full px-6 py-3 font-medium hover:bg-white hover:text-[#0A0A0A] transition-colors active:scale-95">
              Browse items <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/create" data-testid="how-cta-list" className="rounded-full px-6 py-3 font-medium border border-white/20 hover:bg-white/10 transition-colors">
              List an item
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
